// Global audio graph (docs/AUDIO_DESIGN.md §2): music bus with the lofi "record" chain, the shared reverb, vinyl,
// the playlist bus (bypasses the lofi FX), the ambience bus with its street echo, and the master limiter.
import { impulse, crackle, noise, tapeCurve, clipCurve, hold } from './dsp.js';

// Gain staging (tuning knobs). MUSIC_DRIVE sets how hard the tape saturation is hit, MUSIC_MAKEUP restores level after it.
export const MIX = {
  MUSIC_DRIVE: 1.0,
  MUSIC_MAKEUP: 1.45,
  REVERB_RETURN: 0.3,
  CRACKLE: 0.05,          // x dust (0.6-1.4 per track)
  HISS: 0.006,
  ECHO_RETURN: 0.5,
};

export function createMixer(ac, { lite = false, musicVolume = 0.8, ambienceVolume = 0.6, registry = null, dest = ac.destination } = {}) {
  const all = [];
  const add = n => (all.push(n), n);
  const G = (v = 1) => { const g = add(ac.createGain()); g.gain.value = v; return g; };
  const BQ = (type, f, Q) => { const b = add(ac.createBiquadFilter()); b.type = type; b.frequency.value = f; if (Q != null) b.Q.value = Q; return b; };

  /* master: limiter (not a brickwall: auto makeup ~ +1.7 dB) + soft clip */
  const master = G(1);
  const limiter = add(ac.createDynamicsCompressor());
  limiter.threshold.value = -3; limiter.knee.value = 0; limiter.ratio.value = 20;
  limiter.attack.value = 0.003; limiter.release.value = 0.25;
  const clip = add(ac.createWaveShaper());
  clip.curve = clipCurve(); clip.oversample = 'none';
  master.connect(limiter); limiter.connect(clip); clip.connect(dest);

  const musicFader = G(musicVolume * musicVolume), ambFader = G(ambienceVolume * ambienceVolume);
  musicFader.connect(master); ambFader.connect(master);

  /* music: musicSum -> tone LP -> tape sat -> HP 30 -> wow/flutter delay -> musicFader */
  const musicIn = G(1), revIn = G(1), revRet = G(MIX.REVERB_RETURN);
  const conv = add(ac.createConvolver());
  conv.normalize = true;
  conv.buffer = impulse(ac, { sec: lite ? 1.6 : 2.4, rt60: 2 });
  revIn.connect(conv); conv.connect(revRet); revRet.connect(musicIn);
  const tone = BQ('lowpass', 8000, 0.7);
  const drive = G(MIX.MUSIC_DRIVE);
  const sat = add(ac.createWaveShaper());
  sat.curve = tapeCurve(2, 0.08); sat.oversample = lite ? 'none' : '2x';
  const makeup = G(MIX.MUSIC_MAKEUP);
  const hp = BQ('highpass', 30, 0.707);
  const tapeDelay = add(ac.createDelay(0.05));
  tapeDelay.delayTime.value = 0.015;
  musicIn.connect(tone); tone.connect(drive); drive.connect(sat); sat.connect(makeup);
  makeup.connect(hp); hp.connect(tapeDelay); tapeDelay.connect(musicFader);

  const wow = add(ac.createOscillator()), wowDepth = G(0.001);        // ±6 cents at 0.556 Hz ≈ 1.0 ms
  const flutter = add(ac.createOscillator()), flutterDepth = G(2e-5); // ±1.6 cents at 7.3 Hz ≈ 20 µs
  wow.frequency.value = 0.556; flutter.frequency.value = 7.3;
  wow.connect(wowDepth); wowDepth.connect(tapeDelay.delayTime);
  flutter.connect(flutterDepth); flutterDepth.connect(tapeDelay.delayTime);

  /* vinyl: crackle loop + hiss loop -> vinylGain -> musicFader (the engine sets dust and the intro/gap boost) */
  const vinyl = G(1), crackleGain = G(MIX.CRACKLE), hissGain = G(MIX.HISS);
  const crk = add(ac.createBufferSource());
  crk.buffer = crackle(ac); crk.loop = true;
  const hiss = add(ac.createBufferSource());
  hiss.buffer = noise(ac, 'pink'); hiss.loop = true;
  const hissHP = BQ('highpass', 2000, 0.707);
  crk.connect(crackleGain); crackleGain.connect(vinyl);
  hiss.connect(hissHP); hissHP.connect(hissGain); hissGain.connect(vinyl);
  vinyl.connect(musicFader);

  /* playlist decks bypass the lofi chain */
  const playlistIn = G(1);
  playlistIn.connect(musicFader);

  /* ambience bus + street echo (230 / 370 ms taps, feedback .35 through an LP 1.8 kHz) */
  const ambIn = G(1), echoIn = G(1), echoOut = G(MIX.ECHO_RETURN), fb = G(0.35);
  const echoLP = BQ('lowpass', 1800, 0.5);
  const dA = add(ac.createDelay(1)), dB = add(ac.createDelay(1));
  dA.delayTime.value = 0.23; dB.delayTime.value = 0.37;
  echoIn.connect(echoLP); echoLP.connect(dA); echoLP.connect(dB);
  dA.connect(echoOut); dB.connect(echoOut); dB.connect(fb); fb.connect(echoLP);
  echoOut.connect(ambIn);
  ambIn.connect(ambFader);

  const t0 = ac.currentTime;
  wow.start(t0); flutter.start(t0); crk.start(t0); hiss.start(t0, 1.3);
  const voice = registry ? registry.add({ src: [], all, tag: 'mixer' }) : null;

  const fade = (param, v) => { const t = ac.currentTime; hold(param, t); param.setTargetAtTime(v, t, 0.05); };
  return {
    musicIn, revIn, echoIn, ambIn, playlistIn, master,
    tone,                                                    // BiquadFilter: the engine automates tone.frequency
    tape: { wow: wowDepth.gain, flutter: flutterDepth.gain }, // delay-time LFO depths in seconds
    vinyl: { gain: vinyl.gain, crackle: crackleGain.gain, hiss: hissGain.gain },
    taps: { music: tapeDelay, musicPost: musicFader, ambPost: ambFader, preLimiter: master, out: clip },   // for measurements
    setMusicVolume(v) { v = Math.min(1, Math.max(0, +v || 0)); fade(musicFader.gain, v * v); },
    setAmbienceVolume(v) { v = Math.min(1, Math.max(0, +v || 0)); fade(ambFader.gain, v * v); },
    get nodeCount() { return all.length; },
    dispose() {
      for (const s of [wow, flutter, crk, hiss]) { try { s.stop(); } catch { /* not started */ } }
      if (voice) registry.free(voice);
      else for (const n of all) { try { n.disconnect(); } catch { /* ok */ } }
    },
  };
}
